import { OutlinedTextFieldProps, TextField, debounce, styled } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

/** A test applied to a text field value. */
export interface ValidationTest {
  /** The test callback, accepting the new value of the field and returning the valid status (accepted returns true). */
  test: (value: string) => boolean;
  /** The message to be rendered to the user when the test has failed (returned `false`). */
  message: string;
}

export interface ValidatedTextFieldProps extends Omit<OutlinedTextFieldProps, "type" | "onChange" | "variant"> {
  /** The amount of time in miliseconds to debounce changes in value prior to validation and callback execution.
   * Pass `0` to disable debounced execution.
   * @default 400
   */
  debounce?: number;
  /** The tests to be applied to the new state of the field. */
  tests?: ValidationTest[];
  /** The controlled value of the field. This should either be a number or a string (or empty string) when controlling
   * the component. If an uncontrolled desire is behavior, pass `undefined` or do not set.
   */
  value?: string | number;
  /** Handler fired whenever the field's internal value changes, passing either a validated value or `undefined`.
   * @param value A truthy string value of the field, else `undefined` if empty string.
   */
  onAccepted?: (value?: string) => void;
  /** Handler fired whenever the field's internal value changes with the current unvalidated value.
   * @param value The truthy string value of the field, else `undefined` if empty string.
   */
  onChange?: (value?: string) => void;
  /** Callback accepting the new proposed state of the field.
   * Return false to deny all update state handler executions and block the field from accepting the value input entirely. */
  rejectInput?: (value: string) => boolean;
}

export const UnstyledValidatedTextField: React.FC<ValidatedTextFieldProps> = ({
  debounce: debounceTimeMs = 400,
  error,
  helperText,
  tests,
  value,
  onAccepted,
  onChange,
  rejectInput,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState<string>();
  const [validationError, setValidationErrors] = useState<string>();
  const propsRef = useRef({ onAccepted, tests, debounceTimeMs });

  // maintain a ref to the prop used by our useCallback hook
  useEffect(() => {
    propsRef.current = { onAccepted, tests, debounceTimeMs };
  }, [tests, onAccepted, debounceTimeMs]);

  // debounced execution of validation and `onAccepted` callbacks.
  const validateAndCallbacks = useCallback(
    debounce((value: string) => {
      const newValidationError = propsRef.current.tests
        ? propsRef.current.tests.find(validationTest => !validationTest.test(value))?.message
        : undefined;
      setValidationErrors(newValidationError);
      if (propsRef.current.onAccepted)
        propsRef.current.onAccepted(!newValidationError ? value || undefined : undefined);
    }, propsRef.current.debounceTimeMs),
    []
  );

  // passthrough control of the input
  useEffect(() => {
    // if uncontrolled do nothing
    if (value === undefined) return;
    setInternalValue(String(value));
    validateAndCallbacks(String(value));
  }, [value, validateAndCallbacks]);

  // when the text field value changes, update its internal values, onChange callback, and debounce validation
  const onFieldChange = (value: string) => {
    if (rejectInput && rejectInput(value)) return;
    setInternalValue(value);
    if (onChange) onChange(value || undefined);
    validateAndCallbacks(value);
  };

  return (
    <TextField
      value={value !== undefined ? String(value) : internalValue || ""}
      onChange={({ target: { value } }) => onFieldChange(value)}
      // default helper text to a blank space so that its wrapper is always rendered
      helperText={helperText || validationError || " "}
      error={error || Boolean(validationError)}
      {...rest}
    />
  );
};

/** An advanced MUI Text field featuring validation and allowed input features. */
export const ValidatedTextField = styled(UnstyledValidatedTextField)(({ theme }) => ({
  "& .MuiFormHelperText-root": {
    margin: `${theme.spacing(-0.2)} 0 ${theme.spacing(-0.6)} ${theme.spacing(1.9)}`
  },
  "& .Mui-error .MuiOutlinedInput-notchedOutline": { borderWidth: ".15em" }
}));
